import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";

import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import { useAppSelector } from "app/hooks";
import { AssignedTaskTags } from "components/assigned-tags";
import CircularProgress from "components/circular-progress";
import type { StatusTransitionCode } from "components/status-transition-cell/status-transition-cell.component";
import {
  TASK_HISTORY_FEATURE,
  TEAMS_FEATURE,
  TEAM_MENTIONS_FEATURE
} from "constants/feature-flags";
import {
  TASK_STATUS_CANCELLED,
  TASK_STATUS_COMPLETED
} from "constants/staff-tasks";
import { selectCurrentUser } from "features/auth/auth-slice";
import DueDateDisplay from "features/staff-tasks-v2/drawer/staff-task-card/due-date-display.component";
import TaskStatusField from "features/staff-tasks-v2/drawer/staff-task-card/task-status-field-component";
import useMarkStaffTaskMentionsSeen from "features/staff-tasks-v2/hooks/use-mark-staff-task-mentions-seen.hook";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import type { StaffTask } from "services/types/staff-tasks.type";
import { formatBackendDateOnly } from "utils/common";

import FieldLabel from "../../../drawer/staff-task-card/field-label.component";
import PriorityLabel from "../../priority-label.component";
import StaffTaskAttachments from "../staff-task-attachments";
import StaffTaskComments from "./staff-task-comments";
import StaffTaskCompletionDetails from "./staff-task-completion-details.component";
import StaffTaskHistory from "./staff-task-history.component";
import TaskFields from "./task-fields.component";
import TeamTaskBanner from "./team-task-banner.component";

type ViewStaffTaskFormProps = {
  task: StaffTask | undefined;
  isBusy?: boolean;
};

export default function ViewStaffTaskForm({
  task,
  isBusy
}: ViewStaffTaskFormProps) {
  const { handleOpenTaskModal, handleStatusUpdate } = useStaffTasksV2();
  const flags = useFlags();
  const isTaskHistoryEnabled = Boolean(flags[TASK_HISTORY_FEATURE]);
  const isMentionsEnabled = Boolean(flags[TEAM_MENTIONS_FEATURE]);
  const isTeamsEnabled = Boolean(flags[TEAMS_FEATURE]);
  const currentUserId = useAppSelector(selectCurrentUser)?.id;

  useMarkStaffTaskMentionsSeen({
    enabled: isMentionsEnabled,
    task,
    currentUserId
  });

  const handleStatusChange = useCallback(
    (code: StatusTransitionCode) => handleStatusUpdate(Number(task?.id), code),
    [handleStatusUpdate, task?.id]
  );

  if (!task) {
    return <CircularProgress sx={{ width: "100%" }} />;
  }

  const dueDateDisplay = task?.due_at
    ? formatBackendDateOnly(task.due_at, "MMM dd, yyyy")
    : null;

  const isComplete =
    task.status === TASK_STATUS_COMPLETED ||
    task.status === TASK_STATUS_CANCELLED;

  return (
    <Stack rowGap={2} mt={-1}>
      {isTeamsEnabled && task.team && <TeamTaskBanner task={task} />}
      <Stack direction="row">
        <Stack flex={5.5} sx={{ boxSizing: "border-box", pr: 2 }}>
          <Stack direction="row" columnGap={5}>
            <Stack rowGap={1.5}>
              <FieldLabel label="Priority" sx={{ mb: "3px" }} />
              <PriorityLabel
                priority={Number(task.priority)}
                isComplete={isComplete}
              />
            </Stack>
            <Stack rowGap={1.5} width="180px">
              <FieldLabel label="Status" />
              <TaskStatusField
                value={task.status}
                completedAtDate={
                  task.status === TASK_STATUS_COMPLETED
                    ? task.completed_at
                    : undefined
                }
                onChange={handleStatusChange}
              />
            </Stack>

            <AssignedTaskTags task={task} />

            <Stack rowGap={1.5}>
              <FieldLabel label="Due Date" sx={{ pb: "2px" }} />
              <DueDateDisplay
                date={task.due_at}
                showDate={isComplete}
                isComplete={isComplete}
                sx={{ mr: -1 }}
              />
              {!isComplete && dueDateDisplay && (
                <Typography
                  component="div"
                  variant="body1"
                  sx={{
                    color: grey[900],
                    fontSize: "12px",
                    mt: -1,
                    display: "flex",
                    alignSelf: "flex-start",
                    pl: 2
                  }}
                >
                  {dueDateDisplay}
                </Typography>
              )}
            </Stack>
            <Button
              variant="contained"
              startIcon={
                isBusy ? (
                  <CircularProgress sx={{ transform: "scale(0.5)" }} />
                ) : (
                  <EditIcon />
                )
              }
              sx={{ height: "38px", mt: 3.2 }}
              disabled={isBusy}
              onClick={() => {
                handleOpenTaskModal({
                  mode: "edit",
                  taskId: task.id,
                  onCancelMode: "openViewModal"
                });
              }}
            >
              Edit
            </Button>
          </Stack>
          <Stack rowGap={2} mt={2}>
            <TaskFields task={task} />
          </Stack>
          <Box mt={2}>
            <StaffTaskAttachments
              taskId={task.id}
              attachments={task.attachments}
              /* Onboarding tasks accept zip/image/Office; ordinary tasks stay PDF-only. */
              allowExtendedFileTypes={Boolean(task.implementation)}
            />
          </Box>
        </Stack>
        <Box display="flex" flex={5}>
          <StaffTaskComments task={task} isBusy={isBusy} />
        </Box>
      </Stack>
      <StaffTaskCompletionDetails task={task} />
      {isTaskHistoryEnabled && <StaffTaskHistory task={task} />}
    </Stack>
  );
}
